import type { Gender, ListingBedroom, ListingType } from '@prisma/client';
import { CircleF } from '@react-google-maps/api';
import {
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  type SerializeFrom,
  json,
  redirect,
} from '@remix-run/node';
import {
  Link,
  Outlet,
  useFetcher,
  useFormAction,
  useLoaderData,
  useLocation,
} from '@remix-run/react';
import { type FC, useEffect, useState } from 'react';
import { namedAction } from 'remix-utils/named-action';
import { z } from 'zod';
import { FormatDate } from '~/components/format-date.tsx';
import { InfoBanner } from '~/components/info-banner.js';
import { LocationMap } from '~/components/location-map.tsx';
import { Button, type ButtonProps } from '~/components/ui/button.tsx';
import { Card } from '~/components/ui/card.tsx';
import { Icon } from '~/components/ui/icon.tsx';
import { UserAvatar } from '~/components/user-avatar.tsx';
import { amenityIcons } from '~/core/amenities.ts';
import type { Db } from '~/core/db.server.js';
import { useReportListing } from '~/routes/resources+/listing+/$id.report.js';
import { useSidebarData } from '~/routes/resources+/sidebar-data.$mode.js';
import { useChatStore } from '~/stores/chat.store.tsx';
import { useImagesDialogStore } from '~/stores/image-dialog.store.tsx';
import { cn, useIsSubmitting } from '~/utils/misc.ts';
import { badRequest } from '~/utils/responses.server.ts';
import { useEvent } from '~/utils/use-event.js';
import { useFetcherSubmitted } from '~/utils/use-fetcher-submitted.tsx';
import { useUser } from '~/utils/user.ts';
import { zParseOrRedirect } from '~/utils/zod.server.ts';
import type { DashboardClassesHandle } from '../_layout.tsx';
import { CompletePreferencesBanner } from './matches.tsx';

const paramsSchema = z.object({
  id: z.string(),
});

export const handle = {
  dashboardClassName: 'bg-white',
} satisfies DashboardClassesHandle;

export async function loader({ params, context, request }: LoaderFunctionArgs) {
  await context.authSession.requireUser();

  const { id } = zParseOrRedirect(params, paramsSchema, '/app/404');

  const listing = await getListing(context.db, id);

  return {
    mode: 'active' as const,
    listing,
  };
}

export type ListingInfo = Awaited<ReturnType<typeof getListing>>;

export async function getListing(db: Db, id: string) {
  const listing = await db.listing.findUnique({
    where: { id },
    select: {
      id: true,
      status: true,
      amenities: {
        select: { label: true, type: true },
      },
      images: {
        select: {
          file: {
            select: {
              url: true,
            },
          },
        },
      },
      totalBedrooms: true,
      totalBathrooms: true,
      type: true,
      randomizedLatitude: true,
      randomizedLongitude: true,
      bedrooms: {
        select: {
          id: true,
          type: true,
          availableFrom: true,
          availableTo: true,
          price: true,
          status: true,
          sequenceIndex: true,
          images: {
            select: {
              file: {
                select: {
                  url: true,
                },
              },
            },
          },
        },
        orderBy: {
          sequenceIndex: 'asc',
        },
      },
      user: {
        select: {
          id: true,
          firstName: true,
          lastName: true,
          university: true,
          graduationYear: true,
          avatar: {
            select: {
              url: true,
            },
          },
        },
      },
      roommates: {
        select: {
          id: true,
          gender: true,
        },
      },
    },
  });

  if (!listing) {
    throw redirect('/app/404');
  }

  const { randomizedLatitude, randomizedLongitude, ...listingInfo } = listing;

  return {
    ...listingInfo,
    location: {
      lat: randomizedLatitude,
      lng: randomizedLongitude,
      radius: 200,
    },
  };
}

export async function action({ request, params, context }: ActionFunctionArgs) {
  const user = await context.authSession.requireUser();
  const { id: listingId } = zParseOrRedirect(params, paramsSchema, '/app/404');

  return namedAction(request, {
    async message() {
      const listing = await context.db.listing.findUnique({
        where: { id: listingId },
        select: { userId: true },
      });

      if (!listing) {
        throw redirect('/app/404');
      }

      if (user.id === listing.userId) {
        throw badRequest({ ok: true });
      }

      let conversationId = (
        await context.db.conversation.findFirst({
          where: { hostId: listing.userId, guestId: user.id, listingId },
          select: { id: true },
        })
      )?.id;

      if (!conversationId) {
        conversationId = await context.conversation.createConversation({
          hostId: listing.userId,
          guestId: user.id,
          listingId,
        });
      }

      return json({ ok: true, conversationId });
    },
  });
}

export default function Listing() {
  const { listing, mode } = useLoaderData<typeof loader>();

  return (
    <ListingPage listing={listing} mode={mode}>
      <Outlet />
    </ListingPage>
  );
}

type ListingProps = SerializeFrom<typeof loader>['listing'];

type ListingPageProps = {
  listing: ListingProps;
  mode: 'active' | 'draft';
  children: React.ReactNode;
};
export function ListingPage(props: ListingPageProps) {
  const { listing, mode } = props;

  const title = listing.user.firstName.endsWith('s')
    ? `${listing.user.firstName}' room`
    : `${listing.user.firstName}'s room`;

  const isEntirePlace = listing.bedrooms.some((bedroom) => bedroom.type === 'ENTIRE_PLACE');

  const preferencesNotSet = useSidebarData((x) => x.guestData?.preferencesNotSet);

  const user = useUser();
  const isOwnListing = user.id === listing.user.id;

  return (
    <article className="flex flex-col items-center justify-start p-4">
      <div className="h-full w-full max-w-[1200px]">
        {!!preferencesNotSet && !isOwnListing && (
          <CompletePreferencesBanner className="mb-6 ml-1 w-full" />
        )}
        {isOwnListing && <OwnListingBanner listingId={listing.id} listingMode={mode} />}
        <div className="flex w-full flex-row items-center justify-between gap-2">
          <div>
            <h1 className="font-semibold text-xl xs:text-2xl sm:text-4xl md:text-3xl">{title}</h1>
            <h2 className="text-gray-500 text-sm xs:text-base sm:text-lg md:text-xl">
              Private room in a {listing.totalBedrooms}
              {' bedroom '}
              {listing.type === 'HOUSE' ? 'house' : 'apartment'}
            </h2>
          </div>
          <div className="flex flex-row gap-2">
            <DirectMessageButton
              variant="simple"
              size="icon"
              className="h-8 w-8 text-primary"
              user={listing.user}
            >
              <Icon name="conversation" className="h-full w-full" />
            </DirectMessageButton>
          </div>
        </div>
        <div className="mt-6 flex flex-col gap-8">
          <ListingImagesSection images={listing.images} />
          <ListingSectionLinks
            sections={[
              { id: 'overview', label: 'Overview', default: true },
              { id: 'about', label: 'About the place' },
              listing.roommates.length > 0 && {
                id: 'roommates',
                label: 'Roommates',
              },
              !isEntirePlace && {
                id: 'bedrooms',
                label: 'Available rooms',
              },
              listing.amenities.length > 0 && {
                id: 'amenities',
                label: 'Amenities',
              },
              { id: 'location', label: 'Location' },
            ].filter(Boolean)}
          />
          <div className="flex flex-col-reverse justify-between gap-4 lg:flex-row">
            <div className="flex w-full flex-col gap-8">
              <ListingMainInfoSection
                user={listing.user}
                totalBedrooms={listing.totalBedrooms}
                totalBathrooms={listing.totalBathrooms}
                type={listing.type}
                entirePlace={
                  isEntirePlace
                    ? {
                        price: listing.bedrooms[0].price,
                        availableFrom: listing.bedrooms[0].availableFrom,
                        availableTo: listing.bedrooms[0].availableTo,
                      }
                    : false
                }
                roommatesCount={listing.roommates.length}
              />
              {listing.roommates.length > 0 && (
                <ListingRoommatesSection roommates={listing.roommates} />
              )}
              {!isEntirePlace && <ListingBedroomsSection bedrooms={listing.bedrooms} />}
              {listing.amenities.length > 0 && (
                <ListingAmenitiesSection amenities={listing.amenities} />
              )}
            </div>
            <div className="h-auto min-h-full">
              <BookingFormSection user={listing.user} className="sticky top-4">
                {props.children}
              </BookingFormSection>
            </div>
            <div className="lg:hidden">
              <ListingHostedBySection user={listing.user} />
            </div>
          </div>
          <div className="flex flex-col items-center justify-center gap-2 px-2">
            <ListingLocationSection location={listing.location} />

            <ReportListingButton listingId={listing.id} />
          </div>
        </div>
      </div>
    </article>
  );
}

type ReportListingButtonProps = {
  listingId: string;
};
export const ReportListingButton: FC<ReportListingButtonProps> = (props) => {
  const { listingId } = props;
  const { report } = useReportListing(listingId);
  return (
    <Button
      type="submit"
      variant="simple"
      className="flex flex-row items-center justify-center gap-2 text-muted-foreground"
      onClick={report}
    >
      <Icon name="flag" />
      <span className="whitespace-nowrap">Report this listing</span>
    </Button>
  );
};

function ListingBedroomsSection({ bedrooms }: { bedrooms: ListingProps['bedrooms'] }) {
  return (
    <section id="bedrooms" className="rounded-sm border px-6 py-6">
      <h3 className="pb-1 font-semibold text-xl">Available rooms:</h3>
      <div className="grid grid-cols-2 gap-8">
        {bedrooms.map((bedroom) => (
          <ListingBedroomMainInfo key={bedroom.id} bedroom={bedroom} />
        ))}
      </div>
    </section>
  );
}

type ListingBedroomMainInfoProps = {
  bedroom: Pick<ListingBedroom, 'id' | 'status' | 'sequenceIndex'> & {
    availableFrom: string;
    availableTo: string;
    price: string;
    images: {
      file: {
        url: string;
      };
    }[];
  };
};

function ListingBedroomMainInfo({ bedroom }: ListingBedroomMainInfoProps) {
  const { availableTo, availableFrom, price, id, images, sequenceIndex } = bedroom;

  const showImages = useImagesDialogStore((x) => x.showImages);

  const handleImageClick = (index: number) => {
    showImages(
      images.map((x) => ({ url: x.file.url })),
      index,
    );
  };

  const isBooked = bedroom.status === 'BOOKED';

  return (
    <div id={id}>
      <div className={cn({ relative: isBooked })}>
        <img
          onClick={() => handleImageClick(0)}
          className={cn(
            'aspect-video min-h-40 min-w-40 cursor-pointer rounded-lg bg-gray-300 object-cover hover:brightness-90',
            {
              'pointer-events-none opacity-30': isBooked,
            },
          )}
          src={images[0].file.url}
          alt=""
        />
        {isBooked && (
          <p className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] font-semibold">
            Booked
          </p>
        )}
      </div>
      <div className={cn({ 'opacity-30': isBooked })}>
        <p className="mt-4 text-xl">Bedroom {sequenceIndex}</p>
        <ul className="list-disc pl-7 font-lg">
          <li>${price} / month</li>
          <li>
            {'Available: '}
            <FormatDate date={availableFrom} format="MMMM dd" />
            {' - '}
            <FormatDate date={availableTo} format="MMMM dd" />
          </li>
        </ul>
      </div>
    </div>
  );
}

function DirectMessageButton({
  children,
  user: host,
  ...props
}: { children: React.ReactNode; user: ListingProps['user'] } & ButtonProps) {
  const fetcher = useFetcher<typeof action>();
  const formAction = useFormAction();
  const isPending = useIsSubmitting({
    formMethod: 'POST',
    formAction: `${formAction}?/message`,
    state: 'non-idle',
  });

  const openConversation = useChatStore((x) => x.openConversation);
  useFetcherSubmitted(fetcher.state, () => {
    if (!fetcher.data) return;
    if ('conversationId' in fetcher.data) {
      openConversation(fetcher.data.conversationId);
    }
  });

  const user = useUser();

  if (user.id === host.id) {
    return null;
  }

  return (
    <fetcher.Form method="POST" action="?/message">
      <Button
        type="submit"
        variant="simple"
        className="flex flex-row items-center justify-center gap-2 text-black lg:text-lg"
        disabled={isPending}
        {...props}
      >
        {children}
      </Button>
    </fetcher.Form>
  );
}

function BookingFormSection({
  className,
  children,
  user,
}: { className?: string; children: React.ReactNode; user: ListingProps['user'] }) {
  return (
    <div className={cn('flex w-full flex-col gap-4 sm:w-auto', className)}>
      <Card className="flex min-w-64 flex-col gap-4 p-6">{children}</Card>
      <div className="flex w-full flex-row items-center justify-center gap-2 lg:flex-col lg:justify-start lg:px-4">
        <p className="hidden self-start font-semibold text-lg sm:text-xl lg:block">Questions?</p>
        <DirectMessageButton user={user}>
          <Icon name="conversation" />
          <span className="whitespace-nowrap">Message {user.firstName}</span>
        </DirectMessageButton>
      </div>
    </div>
  );
}

type ListingImagesSectionProps = {
  onShowImages?: (index: number) => void;
  images: ListingProps['images'];
};
export function ListingImagesSection({ onShowImages, images }: ListingImagesSectionProps) {
  const showImages = useImagesDialogStore((x) => x.showImages);

  const handleImageClick =
    onShowImages ??
    ((index: number) => {
      showImages(
        images.map((x) => ({ url: x.file.url })),
        index,
      );
    });

  return (
    <section
      id="overview"
      className={cn(
        'relative grid max-h-[min(60vh,600px)] min-h-[400px] overflow-hidden rounded-lg sm:grid-rows-2 sm:gap-2',
        images.length === 2 && 'sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]',
        images.length >= 3 && 'sm:grid-cols-[minmax(0,5fr)_minmax(0,3fr)]',
        images.length >= 5 && 'lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]',
      )}
    >
      <Button
        variant="black-outline"
        className="absolute right-4 bottom-4 z-10 flex h-8 flex-row items-center justify-center gap-1 rounded-sm px-2 text-sm hover:bg-gray-300 hover:opacity-100"
        onClick={() => handleImageClick(0)}
      >
        <Icon name="images" className="h-4 w-4" />
        Show all photos
      </Button>
      {!!images[0] && (
        <GalleryImage
          className="relative sm:row-span-2"
          url={images[0].file.url}
          onClick={() => handleImageClick(0)}
        />
      )}
      {!!images[1] && (
        <GalleryImage
          className={cn('hidden sm:block', images.length === 2 && 'sm:row-span-2')}
          url={images[1].file.url}
          onClick={() => handleImageClick(1)}
        />
      )}
      {!!images[2] && (
        <GalleryImage
          className="hidden sm:block"
          url={images[2].file.url}
          onClick={() => handleImageClick(2)}
        />
      )}
      {!!images[3] && !!images[4] && (
        <>
          <GalleryImage
            className="hidden lg:block"
            url={images[3].file.url}
            onClick={() => handleImageClick(3)}
          />
          <GalleryImage
            className="hidden lg:block"
            url={images[4].file.url}
            onClick={() => handleImageClick(4)}
          />
        </>
      )}
    </section>
  );
}

type GalleryImageProps = {
  className?: string;
  url: string;
  onClick: () => void;
  children?: React.ReactNode;
};

function GalleryImage({ className, url, onClick, children }: GalleryImageProps) {
  return (
    <div
      className={cn('min-h-40 min-w-40 cursor-pointer rounded-lg bg-gray-300', className)}
      onClick={onClick}
    >
      <img className="h-full w-full rounded-lg object-cover hover:brightness-90" src={url} alt="" />
      {children}
    </div>
  );
}

type ListingSectionLinksProps = {
  sections: {
    id: string;
    label: string;
    default?: boolean;
  }[];
};

export function ListingSectionLinks({ sections }: ListingSectionLinksProps) {
  const hash = useLocation().hash;

  const [selectedSection, setSelectedSection] = useState<string | null>(null);

  const handleHashChange = useEvent(() => {
    setSelectedSection(sections.find((section) => !!hash && hash.endsWith(section.id))?.id ?? null);
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(handleHashChange, [hash, handleHashChange]);

  return (
    <div className="hidden w-full flex-row flex-wrap gap-4 rounded-md border px-4 py-2 sm:flex">
      {sections.map((section) => {
        const isSelected =
          selectedSection === section.id || (selectedSection === null && section.default);

        return (
          <Link
            key={section.id}
            to={`#${section.id}`}
            className={cn('h-6 border-primary hover:border-b-2', isSelected && 'border-b-2')}
          >
            {section.label}
          </Link>
        );
      })}
    </div>
  );
}

type ListingHostedBySectionProps = {
  user: {
    firstName: string;
    lastName: string;
    university: string;
    graduationYear: number;
    avatar: {
      url: string;
    } | null;
  };
};
export function ListingHostedBySection({ user }: ListingHostedBySectionProps) {
  return (
    <div className="flex flex-row items-center justify-start gap-4 border-t border-b px-4 py-4">
      <UserAvatar className="h-20 w-20" user={user} />
      <div>
        <p className="text-xl">Hosted by {user.firstName}</p>
        <p className="text-lg">
          {user.university}
          {' ‘'}
          {user.graduationYear % 100}
        </p>
      </div>
    </div>
  );
}

type ListingMainInfoSectionProps = {
  type: ListingType;
  roommatesCount: number;
  user?: ListingHostedBySectionProps['user'];
  totalBedrooms: number;
  totalBathrooms: number;
  entirePlace:
    | false
    | {
        price: number | string;
        availableFrom: string | Date;
        availableTo: string | Date;
      };
};
export function ListingMainInfoSection({
  type,
  roommatesCount,
  user,
  totalBedrooms,
  totalBathrooms,
  entirePlace,
}: ListingMainInfoSectionProps) {
  const roommatesContent = `Your own bedroom in ${type === 'HOUSE' ? 'a house' : 'an apartment'} with ${roommatesCount} ${roommatesCount === 1 ? 'roommate' : 'roommates'}`;

  return (
    <section id="about" className="flex min-h-[182px] flex-col gap-2">
      {!!user && (
        <div className="hidden lg:block">
          <ListingHostedBySection user={user} />
        </div>
      )}
      <p className="pl-8 font-semibold text-xl">About the place:</p>
      <ul className="list-disc pt-2 pl-12">
        <li>
          Private room in a {totalBedrooms} bedroom, {totalBathrooms} bathroom{' '}
          {type === 'HOUSE' ? 'house' : 'apartment'}
        </li>
        {!!entirePlace && (
          <li>
            {'Available: '}
            <FormatDate date={entirePlace.availableFrom} format="MMMM dd, yyyy" />
            {' - '}
            <FormatDate date={entirePlace.availableTo} format="MMMM dd, yyyy" />
          </li>
        )}
        {roommatesCount > 0 && <li>{roommatesContent}</li>}
        {!!entirePlace && <li>Asking price: ${entirePlace.price} / month</li>}
      </ul>
    </section>
  );
}

function ListingRoommatesSection({ roommates }: { roommates: ListingProps['roommates'] }) {
  return (
    <section id="roommates" className="flex flex-col gap-2 rounded-sm border py-4 pr-2 pl-8">
      <p className="font-semibold text-xl">Roommates:</p>
      <div className="flex flex-row flex-wrap gap-4">
        {roommates.map((roommate) => (
          <div key={roommate.id} className="h-12 w-12 overflow-hidden rounded-full p-0">
            <img
              className="h-12 w-12"
              alt={roommate.gender}
              src={getRoommateIcon(roommate.gender)}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function getRoommateIcon(gender: Gender) {
  switch (gender) {
    case 'FEMALE':
      return '/img/icons/gender/female.png';
    case 'MALE':
      return '/img/icons/gender/male.png';
    case 'OTHER':
      return '/img/icons/gender/non-binary.png';
    default:
      gender satisfies never;
      return '';
  }
}

function ListingLocationSection({ location }: { location: ListingProps['location'] }) {
  return (
    <section id="location" className="w-full">
      <h3 className="pb-2 pl-8 font-semibold text-xl">Location:</h3>
      <LocationMap
        zoom={15}
        location={location}
        className="h-[60vh] min-h-[400px] rounded-md sm:h-[460px]"
      >
        <CircleF
          center={location}
          radius={location.radius}
          options={{
            clickable: false,
            strokeColor: '#2EBDB6',
            fillColor: '#2EBDB6',
          }}
        />
      </LocationMap>
      <p className="text-center text-gray-500 text-sm">
        The exact location will be provided after booking
      </p>
    </section>
  );
}

type ListingAmenitiesSectionProps = {
  amenities: {
    type: string;
    label: string;
  }[];
};
export function ListingAmenitiesSection({ amenities }: ListingAmenitiesSectionProps) {
  return (
    <section id="amenities" className="rounded-sm border py-6 pr-2 pl-8">
      <h3 className="pb-1 font-semibold text-xl">Amenities:</h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {amenities.map((amenity) => (
          <div
            key={amenity.type}
            className="borderpx-4 flex select-none flex-row items-center justify-start gap-2 rounded-md py-2 text-center"
          >
            <img
              alt={amenity.type}
              className="h-8 w-8 object-contain"
              src={`/img/icons/${amenityIcons[amenity.type]}`}
            />
            {amenity.label}
          </div>
        ))}
      </div>
    </section>
  );
}

type OwnListingBannerProps = {
  listingMode: 'draft' | 'active';
  listingId: string;
};
function OwnListingBanner(props: OwnListingBannerProps) {
  const { listingMode, listingId } = props;
  return (
    <InfoBanner
      edgeClassName={listingMode === 'draft' ? 'bg-yellow-500' : 'bg-green-500'}
      className="mb-6"
    >
      <p className={listingMode === 'draft' ? 'text-gray-600' : 'text-green-500'}>
        {listingMode === 'draft' ? 'Your listing is not yet active! ' : 'Your listing is active! '}
        Click below to make edits anytime
      </p>
      <Button variant="black-outline" className="w-fit border-border" asChild>
        <Link
          to={
            listingMode === 'draft'
              ? `/app/listing/new/${listingId}/summary`
              : `/app/host/listing/${listingId}/edit`
          }
        >
          Edit listing
        </Link>
      </Button>
    </InfoBanner>
  );
}
